import Button from '@jetbrains/ring-ui/components/button/button'
import Dropdown, {Anchor} from '@jetbrains/ring-ui/components/dropdown/dropdown'
import classNames from 'classnames'
import * as React from 'react'
import {useMemo} from 'react'
import {shallowEqual} from 'react-redux'

import EntityPath from '../../../../../containers/EntityPath'
import {useRunningBuildUpdater} from '../../../../../containers/RunningBuildUpdater'
import {useBooleanQueryParam, useQueryParam} from '../../../../../hooks/routes'
import {useBuildStatusIcon} from '../../../../../hooks/useBuildStatusIcon'
import {getBuild, getBuildStatusType, getBuildType, getProject} from '../../../../../selectors'
import {
  getProjectBuildTypeFilter,
  BuildPageTabNamesEnum,
  stringifyId,
  toBuildId,
  BuildStatusType,
} from '../../../../../types'
import type {NormalizedBuild, ProjectId} from '../../../../../types'
import {parseBranch} from '../../../../../utils/branchNames'
import {emptyArray} from '../../../../../utils/empty'
import {notNull} from '../../../../../utils/guards'
import BuildBranch from '../../../../common/BranchLabel/BuildBranchLabel.container'
import BuildArtifacts from '../../../../common/BuildArtifacts/BuildArtifacts.container'
import BuildCommentByIcon from '../../../../common/BuildCommentByIcon/BuildCommentByIcon.container'
import BuildDuration from '../../../../common/BuildDuration/BuildDuration.container'
import BuildNumberContainer from '../../../../common/BuildNumber/BuildNumber.container'
import {formatBuildNumber} from '../../../../common/BuildNumber/BuildNumber.utils'
import BuildStatus from '../../../../common/BuildStatus/BuildStatus.container'
import buildLinkStyles from '../../../../common/BuildStatus/BuildStatusLink/BuildStatusLink.css'
import {useTitle} from '../../../../common/BuildStatus/BuildStatusLink/BuildStatusLink.utils'
import BuildStatusChangeComment from '../../../../common/BuildStatusChangeComment/BuildStatusChangeComment'
import Icon from '../../../../common/Icon/Icon'
import MiddleEllipsis from '../../../../common/MiddleEllipsis/MiddleEllipsis'
import Popup from '../../../../common/Popup/Popup.lazy'
import OverviewStatusIcon from '../../../../common/ProjectOrBuildTypeIcon/OverviewStatusIcon.container'
import ProjectOrBuildTypeIcon from '../../../../common/ProjectOrBuildTypeIcon/ProjectOrBuildTypeIcon'
import {EntityIconType} from '../../../../common/ProjectOrBuildTypeIcon/ProjectOrBuildTypeIcon.types'
import RouterLink from '../../../../common/RouterLink/RouterLink'
import RunBuild from '../../../../common/RunBuild/RunBuild.container'
import StopBuild from '../../../../common/StopBuild/StopBuild.container'
import SvgIcon from '../../../../common/SvgIcon/SvgIcon'
import type {GraphNode} from '../../../PipelinesPages/utils/graph'

import {ITEM_WIDTH, POINT_SIZE, VERTICAL_ICON_COMPENSATION} from './DependenciesChain.consts'
import {GroupingContext, HighlightContext, IsDependantContext} from './DependenciesChain.contexts'
import {useChain} from './DependenciesChain.hooks'
import type {NodeLabel} from './DependenciesChain.types'
import {isOnlyLeaf, isReused} from './DependenciesChain.utils'

import styles from './DependenciesChain.css'

import {useAppSelector} from 'src/hooks/react-redux'

declare module 'csstype' {
  interface Properties {
    '--tc-chain-item-width'?: string
  }
}

type Props = {
  datum?: NodeLabel
  x?: number
  y?: number
  chainProjectId?: ProjectId | null
  graph: Record<string, GraphNode>
  isChild?: boolean
}
export default function DependenciesChainItem({
  datum,
  x,
  y,
  chainProjectId,
  graph,
  isChild,
}: Props) {
  const selectedBuildIdString = useQueryParam('selectedBuildId')
  const selectedBuildId = selectedBuildIdString != null ? toBuildId(selectedBuildIdString) : null
  const selectedProjectId = useQueryParam('selectedProjectId')
  const {self, buildsByBuildType} = useChain()

  const selfBuildTypeId = self?.buildType
  const buildTypeId = datum?.isProject ? null : datum?.id
  const builds = (buildTypeId != null && buildsByBuildType[buildTypeId]) || emptyArray
  const selectedBuild: NormalizedBuild | null | undefined = builds.find(
    ({id}) => id === selectedBuildId,
  )
  const currentBuild: NormalizedBuild | null | undefined = selectedBuild ?? builds[0]
  const icon = useBuildStatusIcon(currentBuild?.id)
  const buildTypeName = useAppSelector(state => getBuildType(state, buildTypeId)?.name)
  const projectId = useAppSelector(state =>
    datum?.isProject ? datum.projectId : getBuildType(state, buildTypeId)?.projectId,
  )
  const projectName = useAppSelector(state => getProject(state, projectId)?.name)
  const isComposite = useAppSelector(
    state => getBuildType(state, buildTypeId)?.type === 'composite',
  )
  const isFailed = useAppSelector(state => getBuild(state, currentBuild?.id)?.status !== 'SUCCESS')
  const isCanceled = useAppSelector(
    state => getBuild(state, currentBuild?.id)?.canceledInfo != null,
  )
  const isRunning = useAppSelector(state => getBuild(state, currentBuild?.id)?.state === 'running')
  const isQueued = useAppSelector(state => getBuild(state, currentBuild?.id)?.state === 'queued')
  const hasBranch = useAppSelector(state => getBuild(state, currentBuild?.id)?.branchName != null)
  const number = useAppSelector(state => getBuild(state, currentBuild?.id)?.number)
  const highlighted = React.useContext(HighlightContext)
  const highlight = datum?.id != null && highlighted.has(datum.id)
  const stringId = stringifyId(currentBuild?.id)
  const isMultipleBuilds = builds.length > 1
  const grouping = React.useContext(GroupingContext)
  const isOnlyLeafNode = isOnlyLeaf(graph, buildTypeId)
  const isGrouped =
    buildTypeId != null &&
    isComposite &&
    !isOnlyLeafNode &&
    grouping.isGroupedComposite(buildTypeId)
  const getIsDependant = React.useContext(IsDependantContext)
  const isDependant = buildTypeId != null && getIsDependant(buildTypeId)
  const showDetails = useBooleanQueryParam('showDetails')
  const statusType = useAppSelector(state => getBuildStatusType(state, currentBuild?.id))
  const ungroup = isGrouped && buildTypeId != null && (
    <div className={styles.textWrapper}>
      <Button
        className={styles.ungroupButton}
        text
        onClick={() => grouping.groupComposite(buildTypeId, false)}
      >
        {'Ungroup'}
      </Button>
    </div>
  )
  const branch = hasBranch && <BuildBranch className={styles.branch} buildId={currentBuild?.id} />
  const path = !isChild &&
    projectId != null &&
    (datum?.isProject || projectId !== chainProjectId) && (
      <EntityPath
        className={classNames(styles.project, {[styles.projectTitle]: datum?.isProject})}
        withCollapsing
        projectId={projectId}
        withLeafStatusIcon={false}
        projectOrBuildTypeNode={getProjectBuildTypeFilter({
          projectId: chainProjectId,
        })}
        hideIfSameAsFilter={false}
      />
    )

  const statusText = useAppSelector(state => getBuild(state, currentBuild?.id)?.statusText)
  const waitReason = useAppSelector(state => getBuild(state, currentBuild?.id)?.waitReason)
  const startEstimate = useAppSelector(state => getBuild(state, currentBuild?.id)?.startEstimate)
  const title = useTitle({
    statusText,
    isQueued,
    waitReason,
    startEstimate,
  })

  const buildTypeOrder = useAppSelector(
    state =>
      datum?.isProject
        ? getProject(state, datum.projectId)?.buildTypes?.buildType ?? emptyArray
        : emptyArray,
    shallowEqual,
  )
  const orderedChildren = buildTypeOrder
    .map(id => datum?.children?.find(child => child.id === id))
    .filter(notNull)
  const unorderedChildren = datum?.children?.filter(child => !orderedChildren.includes(child)) ?? []
  const allChildren = [...orderedChildren, ...unorderedChildren]
  const reused = useAppSelector(state => isReused(getBuild(state, currentBuild?.id), self))
  const buildNumber = useAppSelector(state => {
    const build = getBuild(state, currentBuild?.id)
    return formatBuildNumber({
      number: build?.number,
      isQueued: build?.state === 'queued',
      queuePosition: build?.queuePosition,
    }).text
  })

  const iconElement = useMemo(
    () =>
      currentBuild != null && (
        <>
          <Icon
            name={icon}
            className={classNames(styles.icon, {
              [styles.highlightedSelectedIcon]: highlight && selectedBuild != null,
              [styles.withStack]: isMultipleBuilds,
              [styles.reused]: reused,
            })}
          />
          {isMultipleBuilds && (
            <SvgIcon
              icon="stack"
              className={classNames(styles.stack, {
                [styles.highlightedStack]: highlight,
              })}
            />
          )}
        </>
      ),

    [currentBuild, highlight, icon, isMultipleBuilds, reused, selectedBuild],
  )

  const runBuild = useMemo(
    () =>
      buildTypeId != null && (
        <div className={styles.runIconButtonWrapper}>
          <RunBuild
            icon
            buildTypeId={buildTypeId}
            promoteId={self?.id}
            branch={self?.defaultBranch !== true ? parseBranch(self?.branchName) : null}
            className={styles.runIconButton}
            popupClassName={styles.runPopup}
          />
        </div>
      ),
    [buildTypeId, self],
  )

  const buildTypeLabel = useMemo(
    () => (
      <span
        className={classNames(styles.buildTypeLabel, {
          [styles.selfBuildTypeLabel]: buildTypeId === selfBuildTypeId,
        })}
      >
        {isChild && !showDetails && iconElement}
        {isComposite && <ProjectOrBuildTypeIcon type={EntityIconType.BUILD_TYPE} composite />}
        <MiddleEllipsis
          data-test-chain-item-buildtype
          className={classNames(styles.buildTypeEllipsis, {
            [styles.buildTypeEllipsisComposite]: isComposite,
            [styles.buildTypeEllipsisChild]: isChild,
          })}
        >
          {buildTypeName}
        </MiddleEllipsis>
      </span>
    ),
    [buildTypeId, buildTypeName, iconElement, isChild, isComposite, selfBuildTypeId, showDetails],
  )

  const details = currentBuild != null && showDetails && (
    <>
      {!isChild && <br />}
      <div
        className={classNames(
          styles.status,
          statusType !== BuildStatusType.QUEUED ? buildLinkStyles[statusType] : undefined,
        )}
      >
        {isChild && iconElement}
        <span className={styles.textWrapper}>
          {buildNumber}
          {': '}
          {title}
        </span>
      </div>
      {isRunning && (
        <>
          <BuildDuration className={styles.progress} buildId={currentBuild.id} />
          <StopBuild buildId={currentBuild.id} />
        </>
      )}
    </>
  )

  useRunningBuildUpdater(currentBuild?.id, !isRunning)

  const buildTypeDropdown = useMemo(
    () =>
      buildTypeId != null && (
        <Dropdown
          className={styles.buildTypeDropdown}
          anchor={<Anchor className={styles.buildTypeAnchor}>{buildTypeLabel}</Anchor>}
        >
          {props => (
            <Popup {...props} className={styles.popup}>
              {isComposite && !isOnlyLeafNode && (
                <Button
                  onClick={() => {
                    grouping.groupComposite(buildTypeId, !isGrouped)
                    props.onCloseAttempt?.()
                  }}
                  text
                  className={styles.groupUngroupButton}
                >{`${isGrouped ? 'Ungroup' : 'Group'} this composite build`}</Button>
              )}
              {!isChild && (
                <EntityPath
                  className={styles.path}
                  buildTypeId={buildTypeId}
                  withLeafStatusIcon={false}
                  projectOrBuildTypeNode={{
                    nodeType: 'all',
                  }}
                />
              )}
              {builds.map(build => (
                <div className={styles.build} key={stringifyId(build.id)}>
                  <div className={styles.buildTopRow}>
                    <BuildNumberContainer
                      hideStar
                      buildId={build.id}
                      withComment
                      withStatusChangeComment
                    />
                    <BuildBranch className={styles.buildBranch} buildId={build.id} />
                    <div className={styles.runBuild}>
                      <RunBuild
                        buildTypeId={buildTypeId}
                        rerunBuildId={build.id}
                        redirectToQueuedBuild={!isDependant}
                        redirectToQueuedBuildTab={BuildPageTabNamesEnum.DEPENDENCIES}
                      />
                    </div>
                  </div>
                  <BuildStatus buildId={build.id} />
                </div>
              ))}
            </Popup>
          )}
        </Dropdown>
      ),
    [
      buildTypeId,
      buildTypeLabel,
      builds,
      grouping,
      isChild,
      isComposite,
      isDependant,
      isGrouped,
      isOnlyLeafNode,
    ],
  )

  return datum != null && (isChild || (x != null && y != null)) ? (
    <div
      data-chain-item-id={buildTypeId}
      data-test-build-status={statusType}
      className={classNames(styles.item, {
        [styles.child]: isChild,
        [styles.withDetails]: showDetails,
        [styles.projectWrapper]: datum.isProject,
        [styles.selectedProjectWrapper]: datum.isProject && highlight,
      })}
      style={{
        left: x != null ? x - POINT_SIZE / 2 : undefined,
        top: y != null ? y - POINT_SIZE / 2 - VERTICAL_ICON_COMPENSATION : undefined,
        '--tc-chain-item-width': `${ITEM_WIDTH}px`,
      }}
    >
      {isChild ? (
        details
      ) : currentBuild != null ? (
        <RouterLink
          params={{
            selectedProjectId: null,
            selectedBuildId: stringId !== selectedBuildIdString ? stringId : null,
          }}
          withMergeParams
          className={classNames(styles.link, {
            [styles.failed]: isFailed,
            [styles.canceled]: isCanceled,
            [styles.queued]: isQueued,
          })}
          aria-label={`Build #${number ?? ''} in ${buildTypeName ?? ''}`}
        >
          {iconElement}
          {details}
        </RouterLink>
      ) : datum.isProject ? (
        <RouterLink
          params={{
            selectedBuildId: null,
            selectedProjectId: datum.projectId !== selectedProjectId ? datum.projectId : null,
          }}
          withMergeParams
          aria-label={projectName ?? 'Unknown project'}
        >
          <OverviewStatusIcon
            type={EntityIconType.PROJECT}
            id={datum.projectId}
            size="M"
            className={classNames(styles.projectIcon, {
              [styles.selectedProjectIcon]: datum.projectId === selectedProjectId,
            })}
          />
        </RouterLink>
      ) : datum.unrelatedCount != null ? (
        <div className={styles.unrelated} />
      ) : (
        runBuild
      )}
      <div>
        {isChild && currentBuild == null && runBuild}
        {datum.isProject && projectId != null ? (
          <div className={styles.projectNameWrapper}>
            {path}
            <Button
              className={styles.ungroupProjectButton}
              text
              onClick={() => grouping.groupProject(projectId, false)}
            >
              {'Ungroup'}
            </Button>
          </div>
        ) : datum.unrelatedCount != null ? (
          <span className={styles.buildTypeLabel}>{`${datum.unrelatedCount} unrelated`}</span>
        ) : (isComposite && !isOnlyLeafNode) || !isChild || builds.length > 0 ? (
          buildTypeDropdown
        ) : (
          buildTypeLabel
        )}
      </div>
      {!datum.isProject &&
        (showDetails ? (
          <>
            {path}
            <div className={styles.artifactsBranch}>
              {currentBuild != null && (
                <>
                  <BuildArtifacts className={styles.artifacts} buildId={currentBuild.id} />
                  <BuildCommentByIcon buildId={currentBuild.id} className={styles.comment} />
                  <BuildStatusChangeComment buildId={currentBuild.id} className={styles.comment} />
                </>
              )}
              {branch}
            </div>
            {ungroup}
          </>
        ) : (
          ungroup || (highlight && branch) || path
        ))}
      {allChildren.map(child => (
        <DependenciesChainItem
          key={child.id}
          isChild
          graph={graph}
          datum={child}
          chainProjectId={chainProjectId}
        />
      ))}
    </div>
  ) : null
}
